/**
 * Bosh sahifa (Landing Page) bloklarining zaxira kontenti — ЧТЗ 4.2.11.1.
 *
 * Backend ulangan konturda bu ma'lumotlar CMS'dan keladi:
 *   * 1-blok — `GET /portal/public/about/` (sahifalar, rahbariyat, tuzilma, statistika);
 *   * 2-blok — `GET /portal/public/` (bannerlar + yangiliklar, gov.uz/urmon integratsiyasi);
 *   * 3-blok — `GET /portal/public/guides/`;
 *   * 5-blok — `GET /portal/public/media/`;
 *   * 6-blok — `PortalSettings` (do'kon havolalari, belgilar) + dinamik QR.
 *
 * Bu yerdagi qiymatlar faqat server bilan aloqa bo'lmaganda ko'rsatiladi.
 * Raqamli ko'rsatkichlar «namunaviy» deb belgilanadi — ular rasmiy statistika
 * sifatida taqdim etilmaydi.
 */
import type { IconName } from "@/shared/ui/Icon";

/* ------------------------------------------------------------------ 2-blok */

export interface CarouselSlide {
  id: string;
  kind: "news" | "event" | "alert" | "banner";
  tag: string;
  title: string;
  text: string;
  image: string;
  url: string;
  date: string;
  external: boolean;
}

export const carouselFallback: CarouselSlide[] = [
  {
    id: "fallback-planting",
    kind: "event",
    tag: "Ekotadbir",
    title: "Kuzgi ko'chat ekish mavsumi boshlandi",
    text:
      "Hududlarda o'rmon barpo qilish ishlari rejasi e'lon qilindi. Ekish natijalari " +
      "platformada ochiq ma'lumot sifatida joylanadi.",
    image: "/img/band-planting.jpg",
    url: "#media",
    date: "2026-09-15",
    external: false,
  },
  {
    id: "fallback-monitoring",
    kind: "news",
    tag: "Monitoring",
    title: "Sun'iy yo'ldosh tahlili yangi hududlarga kengaytirildi",
    text: "Qoraqalpog'iston va Buxoro bo'yicha o'zgarishlarni avtomatik aniqlash yoqildi.",
    image: "/img/band-boundary.jpg",
    url: "#map",
    date: "2026-08-04",
    external: false,
  },
  {
    id: "fallback-lease",
    kind: "news",
    tag: "Xizmatlar",
    title: "Ijara arizasi endi to'liq onlayn topshiriladi",
    text: "Qog'oz hujjatlar bekor qilindi, imzo OneID orqali elektron tarzda qo'yiladi.",
    image: "/img/band-parcels.jpg",
    url: "#services",
    date: "2026-07-28",
    external: false,
  },
  {
    id: "fallback-nursery",
    kind: "event",
    tag: "Yashil makon",
    title: "Ko'chatxonalar mavsumga tayyor: navlar katalogi yangilandi",
    text: "«Yashil olam» maydonchasida ko'chatlarga onlayn buyurtma berish mumkin.",
    image: "/img/band-nursery.jpg",
    url: "#services",
    date: "2026-07-15",
    external: false,
  },
];

/* ------------------------------------------------------------------ 1-blok */

export const aboutFallback = {
  title: "«Aqlli o'rmon» — Smart Forestry",
  purpose:
    "O'zbekiston Respublikasi O'rmon xo'jaligi agentligining o'rmon fondi, monitoring va " +
    "elektron xizmatlarini yagona ma'lumot manbai asosida birlashtiruvchi davlat axborot tizimi.",
  tasks: [
    "O'rmon fondi yerlarining yagona raqamli xaritasini yuritish",
    "Yong'in, noqonuniy kesish va boshqa hodisalarni real vaqtda aniqlash",
    "Ijara, auksion, ruxsatnoma va to'lovlarni onlayn ko'rsatish",
    "O'rmon barpo qilish va ko'chat ekish natijalarini ochiq e'lon qilish",
    "Fuqarolar murojaatlarini qabul qilish va ijrosini nazorat qilish",
  ],
};

export interface PlatformPillar {
  code: string;
  icon: IconName;
  title: string;
  text: string;
  features: string[];
  href: string;
}

/** Platforma tarkibi — uchta asosiy yo'nalish (ЧТЗ 4.2.11.1, 1-blok). */
export const platformPillars: PlatformPillar[] = [
  {
    code: "gis",
    icon: "map",
    title: "GIS — geoaxborot tizimi",
    text: "O'rmon fondi, kvartal va uchastkalarning yagona raqamli xaritasi.",
    features: ["Ochiq xarita qatlamlari", "NDVI va sun'iy yo'ldosh tahlili", "Kadastr bilan integratsiya"],
    href: "#map",
  },
  {
    code: "kuzatuv",
    icon: "camera",
    title: "Kuzatuv — monitoring",
    text: "Kamera, sensor va dron ma'lumotlari hamda fuqaro murojaatlari bir markazda.",
    features: ["Yong'in xavfi ogohlantirishi", "SLA taymerlari", "Murojaat trekingi"],
    href: "#appeal",
  },
  {
    code: "services",
    icon: "globe",
    title: "Elektron xizmatlar",
    text: "OneID orqali yagona kirish: ijara, auksion, ruxsatnoma va to'lovlar.",
    features: ["ruxsatnoma-urmon.uz", "licenses.uz, e-auksion", "«Yashil olam» savdosi"],
    href: "#services",
  },
];

/** Tuzilma — tizimdagi tashkilotlar reyestri (`accounts.Organization`) bilan bir xil. */
export const structureFallback = {
  root: "O'rmon xo'jaligi agentligi",
  rootHead: "Agentlik direktori",
  regional: [
    "Qoraqalpog'iston Respublikasi",
    "Andijon viloyati",
    "Buxoro viloyati",
    "Jizzax viloyati",
    "Qashqadaryo viloyati",
    "Navoiy viloyati",
    "Namangan viloyati",
    "Samarqand viloyati",
    "Surxondaryo viloyati",
    "Sirdaryo viloyati",
    "Toshkent viloyati",
    "Farg'ona viloyati",
    "Xorazm viloyati",
    "Toshkent shahri",
  ].map((region) => `${region} o'rmon xo'jaligi boshqarmasi`),
};

export interface StatisticItem {
  code: string;
  label: string;
  value: number | null;
  unit: string;
  icon: IconName;
  display: "counter" | "percent" | "gauge";
  max: number;
  source: string;
  asOf: string | null;
  /** Hisobot davri. */
  period?: string;
  showInHero: boolean;
  /** Globusdagi yozuv: ko'rsatiladimi va qisqa nomi (admin panelida boshqariladi). */
  showOnGlobe?: boolean;
  globeLabel?: string;
  /** Zaxira (namunaviy) qiymat — serverdan kelmagan. */
  sample?: boolean;
}

/**
 * Ochiq statistika — namunaviy qiymatlar (server javob bermaganda).
 * Haqiqiy qiymatlar tizim bazasidan (`PublicStatistic.metric`) hisoblanadi.
 */
export const statisticsFallback: StatisticItem[] = [
  { code: "forest-fund-area", label: "O'rmon fondi yerlari", value: 11_200_000, unit: "ga",
    icon: "tree", display: "counter", max: 100, source: "", asOf: null, showInHero: true, sample: true },
  { code: "forest-fund-share", label: "O'rmon fondining respublika hududidagi ulushi", value: 24.9,
    unit: "%", icon: "globe", display: "percent", max: 100, source: "", asOf: null,
    showInHero: true, sample: true },
  { code: "ndvi-avg", label: "Yashillik darajasi (o'rtacha NDVI)", value: 0.41, unit: "",
    icon: "leaf", display: "gauge", max: 1, source: "", asOf: null, showInHero: false, sample: true },
  { code: "trees-planted-year", label: "Joriy yilda ekilgan ko'chatlar", value: 125_000_000,
    unit: "dona", icon: "sparkles", display: "counter", max: 100, source: "", asOf: null,
    showInHero: true, sample: true },
  { code: "nurseries-count", label: "Ko'chatxonalar", value: 210, unit: "ta", icon: "package",
    display: "counter", max: 100, source: "", asOf: null, showInHero: true, sample: true },
  { code: "appeals-resolved", label: "Hal etilgan murojaatlar (joriy yil)", value: 92.4, unit: "%",
    icon: "checkCircle", display: "percent", max: 100, source: "", asOf: null, showInHero: false,
    sample: true },
];

/* ------------------------------------------------------------------ 3-blok */

export interface GuideFallback {
  id: string;
  audience: "individual" | "legal_entity";
  format: "pdf" | "video";
  title: string;
  text: string;
}

export const guidesFallback: GuideFallback[] = [
  { id: "g1", audience: "individual", format: "pdf", title: "Murojaat yuborish yo'riqnomasi",
    text: "Xaritada joyni belgilash, foto/video biriktirish va SMS orqali ID olish." },
  { id: "g2", audience: "individual", format: "video", title: "Murojaat holatini kuzatish",
    text: "ID va telefon raqamining oxirgi 4 raqami yoki SMS-kod bilan tekshirish." },
  { id: "g3", audience: "individual", format: "pdf", title: "Ochiq xaritadan foydalanish",
    text: "Qatlamlar, filtrlar va «Mening joylashuvim» funksiyasi." },
  { id: "g4", audience: "legal_entity", format: "pdf", title: "Ijaraga ariza berish reglamenti",
    text: "OneID orqali kirish, uchastkani tanlash va shartnomani imzolash." },
  { id: "g5", audience: "legal_entity", format: "video", title: "E-auksionda ishtirok etish",
    text: "Uchastkalar lotlari, zakalat va natijalarni rasmiylashtirish." },
  { id: "g6", audience: "legal_entity", format: "pdf", title: "«Yashil olam»da sotuvchi bo'lish",
    text: "Mahsulotlarni katalogga joylash, buyurtma va to'lovlar." },
];

/* ------------------------------------------------------------------ 5-blok */

export interface MediaFallback {
  id: string;
  kind: "photo" | "video";
  album: string;
  albumTitle: string;
  title: string;
  image: string;
}

export const mediaFallback: MediaFallback[] = [
  { id: "m1", kind: "photo", album: "planting", albumTitle: "Ko'chat ekish",
    title: "Kuzgi ekish mavsumi", image: "/img/band-planting.jpg" },
  { id: "m2", kind: "photo", album: "planting", albumTitle: "Ko'chat ekish",
    title: "Ko'chatxonada tayyorgarlik", image: "/img/band-nursery.jpg" },
  { id: "m3", kind: "photo", album: "drone", albumTitle: "Dron monitoringi",
    title: "Uchastka chegaralarini dron bilan aniqlash", image: "/img/band-boundary.jpg" },
  { id: "m4", kind: "photo", album: "drone", albumTitle: "Dron monitoringi",
    title: "Kvartallarni aerofotosuratga olish", image: "/img/band-compartments.jpg" },
  { id: "m5", kind: "photo", album: "drone", albumTitle: "Dron monitoringi",
    title: "Kuzatuv qurilmalari tarmog'i", image: "/img/band-devices.jpg" },
  { id: "m6", kind: "photo", album: "protection", albumTitle: "Muhofaza",
    title: "Yong'inga qarshi mashg'ulot", image: "/img/band-fire.jpg" },
  { id: "m7", kind: "photo", album: "protection", albumTitle: "Muhofaza",
    title: "Ijara uchastkalari nazorati", image: "/img/band-parcels.jpg" },
];

/* ------------------------------------------------------------------ 6-blok */

export const mobileAppFallback = {
  title: "Smart Forestry mobil ilovasi",
  text:
    "Murojaatni joyning o'zidan yuboring, holatini kuzating, yong'in xavfi haqida " +
    "ogohlantirish oling va ochiq xaritadan oflayn foydalaning.",
  badges: ["Bepul", "O'zbekcha", "Русский"],
  features: [
    "GPS bilan aniq joyni belgilash",
    "Foto va video biriktirish",
    "Push-ogohlantirishlar",
  ],
};

/* ------------------------------------------------------------ xarita */

export interface RegionOption {
  code: string;
  name: string;
  lat: number;
  lon: number;
  zoom: number;
}

/** Hududlar markazlari — publik xaritada qidiruv (server javob bermaganda). */
export const regionsFallback: RegionOption[] = [
  { code: "1703", name: "Qoraqalpog'iston Respublikasi", lat: 43.2, lon: 59.3, zoom: 7 },
  { code: "1706", name: "Andijon viloyati", lat: 40.75, lon: 72.35, zoom: 9 },
  { code: "1710", name: "Buxoro viloyati", lat: 40.1, lon: 63.9, zoom: 8 },
  { code: "1714", name: "Jizzax viloyati", lat: 40.3, lon: 67.6, zoom: 8 },
  { code: "1718", name: "Qashqadaryo viloyati", lat: 38.8, lon: 66.0, zoom: 8 },
  { code: "1722", name: "Navoiy viloyati", lat: 41.4, lon: 64.4, zoom: 7 },
  { code: "1726", name: "Namangan viloyati", lat: 41.0, lon: 71.3, zoom: 9 },
  { code: "1730", name: "Samarqand viloyati", lat: 39.65, lon: 66.6, zoom: 8 },
  { code: "1733", name: "Surxondaryo viloyati", lat: 38.0, lon: 67.5, zoom: 8 },
  { code: "1735", name: "Sirdaryo viloyati", lat: 40.45, lon: 68.75, zoom: 9 },
  { code: "1727", name: "Toshkent viloyati", lat: 41.1, lon: 69.9, zoom: 8 },
  { code: "1737", name: "Farg'ona viloyati", lat: 40.4, lon: 71.6, zoom: 9 },
  { code: "1738", name: "Xorazm viloyati", lat: 41.45, lon: 60.8, zoom: 9 },
  { code: "1726401", name: "Toshkent shahri", lat: 41.31, lon: 69.28, zoom: 11 },
];

/** Publik xarita podkladkasi (server javob bermaganda). */
export const publicBasemapFallback = {
  code: "osm",
  name: "OpenStreetMap",
  url_template: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution: "© OpenStreetMap",
};

/** Publik qatlamlar ranglari — `MapStyle` (3-blok) bo'lmaganda. */
export const publicLayerColors: Record<string, string> = {
  forest_fund: "#16a34a",
  nurseries: "#0d9488",
  fire_risk: "#dc2626",
  lease_plots: "#d97706",
  auction_plots: "#7c3aed",
  vegetation_index: "#65a30d",
};

/* ------------------------------------------------------------ baholash */

export const feedbackOptions: Array<{
  value: "excellent" | "good" | "poor";
  label: string;
  hint: string;
  icon: IconName;
}> = [
  { value: "excellent", label: "A'lo", hint: "Hammasi tushunarli va qulay", icon: "checkCircle" },
  { value: "good", label: "Yaxshi", hint: "Qulay, lekin yaxshilash mumkin", icon: "check" },
  { value: "poor", label: "Qoniqarsiz", hint: "Kerakli ma'lumotni topa olmadim", icon: "alert" },
];
